import { css, html } from 'lit';
import { ShuffleStack, Stack } from '../modules/datastructures';
import { KryptonEventDetail } from './krypton-event-detail';
import { PageElement } from './page-element';

class PageStack {

    stack: Stack<PageElement>;

    constructor(allowDuplicates: boolean = true) {
        this.stack = allowDuplicates ? new Stack() : new ShuffleStack();
    }

    get topPage() {
        return this.stack.peek();
    }

    get bottomPage() {
        return this.stack.peekBottom();
    }

    addTop(pageElement: PageElement) {
        if (!pageElement)
            return;
        this.stack.enqueue(pageElement);
    }

    addBottom(pageElement: PageElement) {
        if (!pageElement)
            return;
        this.stack.enqueueBottom(pageElement);
    }

    insertBefore(pageElement: PageElement, before: PageElement) {
        const result = this.stack.insertBefore(pageElement, before);
        return result;
    }

    insertAfter(pageElement: PageElement, after: PageElement) {
        const result = this.stack.insertAfter(pageElement, after);
        return result;
    }

    remove(pageElement: PageElement) {
        const removed = this.stack.dequeue();
        return removed;
    }

    removeTop() {
        return this.stack.dequeue();
    }

    contains(pageElement: PageElement) {
        return this.stack.contains(pageElement);
    }

    size() {
        return this.stack.size();
    }

    clear() {
        this.stack.clear();
    }

}

class PageStackManager {

    pageStack: PageStack = new PageStack();
    forwardPageStack: PageStack = new PageStack();

    async view(pageElement: PageElement, record: boolean = true): Promise<any> {
        if (!pageElement)
            return;
        if (this.pageStack.topPage == pageElement && pageElement.isVisible()) {
            //console.log("already vsiible", pageElement);
            return;
        }
        //console.log("viewing page and adding to history", pageElement);
        this.pageStack.addTop(pageElement);
        this.forwardPageStack.clear();
    }

    async back(): Promise<any> {
        if (!this.hasPast())
            return;
        // if (this.pageStack.topPage?.pin)
        //     return;
        const pageToHide = this.pageStack.removeTop();
        // const pageOwner = pageToHide?.parentElement;
        console.log("back", "pageToHide", pageToHide);
        if (pageToHide) {
            //console.log("back hiding", pageToHide);
            pageToHide.open = false; //TODO setting open in PageStackManager feels incorrect. It should only manage the stacks. Maybe use events to handle this?
            // pageOwner?.removeChild(pageToHide);
            this.forwardPageStack.addTop(pageToHide);
        }
        const pageToShow = this.pageStack.topPage;
        console.log("back", "pageToShow", pageToHide);
        if (pageToShow) {
            pageToShow.open = true;
            // pageOwner?.appendChild(pageToShow);
        }
    }

    async forward() {
        if (!this.hasFuture())
            return;
        const pageToHide = this.pageStack.topPage;
        // const pageOwner = pageToHide?.parentElement;
        if (pageToHide) {
            //console.log("forward hiding", pageToHide);
            pageToHide.open = false;
            // pageOwner?.removeChild(pageToHide);
        }
        const pageToShow = this.forwardPageStack.removeTop();
        if (pageToShow) {
            this.pageStack.addTop(pageToShow);
            pageToShow.open = true;
            // pageOwner?.appendChild(pageToShow);
        }
    }

    hasPast(): boolean {
        return this.pageStack.size() > 1;
    }

    hasFuture(): boolean {
        return this.forwardPageStack.size() >= 1;
    }

    remove(pageElement: PageElement) {
        this.pageStack.remove(pageElement);
        this.forwardPageStack.remove(pageElement);
    }

    contains(pageElement: PageElement) {
        return this.pageStack.contains(pageElement);
    }

}

export class PageManager extends PageElement {

    pageStackManager: PageStackManager = new PageStackManager();

    get topPage(): PageElement | undefined {
        return this.pageStackManager.pageStack.topPage;
    }

    get bottomPage(): PageElement | undefined {
        return this.pageStackManager.pageStack.bottomPage;
    }

    get pageElements(): PageElement[] {
        //return [].slice.call(this.querySelectorAll("page-element"));
        return this.getNestedSlotElements(this.shadowRoot?.querySelector("slot") as HTMLSlotElement, PageElement) as PageElement[];
    }

    size() {
        //return this.querySelectorAll("page-element").length;
        return this.pageElements.length;
    }

    get lastAction(): string {
        return (this.topPage as PageElement).action;
    }

    async back(): Promise<any> {
        if (this.pageStackManager.hasPast())
            return this.pageStackManager.back();
        console.log("cannot go back");
    }

    async forward(): Promise<any> {
        if (this.pageStackManager.hasFuture())
            return this.pageStackManager.forward();
        console.log("cannot go forward");
    }

    removePage(pageElement: PageElement) {
        this.pageStackManager.remove(pageElement);
    }

    clear() {
        this.pageStackManager = new PageStackManager();
        this.pageElements.forEach(p => {
            p.parentElement?.removeChild(p)
        });
    }

    isVisible(): boolean {
        return super.isVisible() && !!this.topPage?.isVisible();
    }

    onSlotChange(e: Event) {
        //console.log(e);
        //const slot = this.shadowRoot?.querySelector("slot");
        //const pageElements = slot?.assignedNodes().filter(n => n instanceof PageElement && !this.pageStackManager.contains(n)) as PageElement[]; //use assignedNodes because IE doesn't support assignedElements

        const pageElements: PageElement[] = this.getNestedSlotElements(this.shadowRoot?.querySelector("slot") as HTMLSlotElement, PageElement) as PageElement[];

        if (!pageElements)
            return;
        // const pageElements = elements.filter(n => n instanceof PageElement || n instanceof PageManagerElement) as PageElement[];
        if (!pageElements)
            return;
        pageElements.forEach(pe => {
            pe.manager = this;
            // if (pe.parentElement != this)
            //     this.appendChild(pe);
            // if (pe.pin)
            //     pe.open = true;
            // else
            if (pe.open) {
                pe.animateShow();
                this.pageStackManager.view(pe);
            }
        });
    }

    onPageElementConnected(e: CustomEvent) {
        if (e.detail.name === KryptonEventDetail.PAGE_ELEMENT_CONNECTED) {
            const nextPage = e.target;
            this.async(() => {
                let handler: Function | undefined;
                while (handler = this.nextPageHandlers.shift()) {
                    handler(nextPage);
                }
            }, 0, "nextPageHandlers");
        }
    }

    static styles = css`
    :host {
        display: block;
        position: relative;
    }
    :host(.pin) {
        height: auto !important;
        min-height: auto !important;
    }`;

    render() {
        return html`<slot @slotchange="${this.onSlotChange}" @page-element-connected="${this.onPageElementConnected}"></slot>`
    }

}

declare global {
    interface HTMLElementTagNameMap {
        'page-manager': PageManager;
    }
}
